import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	IconButton,
	Link,
	List,
	ListItem,
	ListItemText,
	Stack,
	Typography,
} from '@mui/material';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSnackbar } from 'notistack';
import {
	makeApiRequest,
	makeMultipartApiRequest,
} from '../../../../Shared/Api';
import SectionHeader from './SectionHeader';
import { surfaceCard } from '../../../../Shared/surfaceCard';

const FILE_MAX_BYTES = 10 * 1024 * 1024;

const formatFileSize = (bytes) => {
	if (!bytes) {
		return '';
	}
	if (bytes < 1024) {
		return `${bytes} B`;
	}
	if (bytes < 1024 * 1024) {
		return `${(bytes / 1024).toFixed(1)} KB`;
	}
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const JobFiles = ({ jobId }) => {
	const { enqueueSnackbar } = useSnackbar();
	const inputRef = useRef(null);
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [uploading, setUploading] = useState(false);
	const [deletingId, setDeletingId] = useState(null);

	const fetchFiles = useCallback(async () => {
		setLoading(true);
		try {
			const response = await makeApiRequest('GET', '/files/', null, {
				job: jobId,
			});
			setFiles(Array.isArray(response) ? response : []);
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to load job files.', {
				variant: 'error',
			});
		} finally {
			setLoading(false);
		}
	}, [jobId, enqueueSnackbar]);

	useEffect(() => {
		if (jobId) {
			fetchFiles();
		}
	}, [jobId, fetchFiles]);

	const handleChooseFile = () => {
		inputRef.current?.click();
	};

	const handleFileChange = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = '';
		if (!file) {
			return;
		}

		if (file.size > FILE_MAX_BYTES) {
			enqueueSnackbar('Files must be 10 MB or smaller.', { variant: 'error' });
			return;
		}

		setUploading(true);
		try {
			const formData = new FormData();
			formData.append('file', file);
			formData.append('job', jobId);
			await makeMultipartApiRequest('POST', '/files/', formData);
			enqueueSnackbar('File uploaded.', { variant: 'success' });
			await fetchFiles();
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to upload file.', {
				variant: 'error',
			});
		} finally {
			setUploading(false);
		}
	};

	const handleDelete = async (fileId) => {
		setDeletingId(fileId);
		try {
			await makeApiRequest('DELETE', `/files/${fileId}/`);
			enqueueSnackbar('File deleted.', { variant: 'success' });
			setFiles((prev) => prev.filter((file) => file.id !== fileId));
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to delete file.', {
				variant: 'error',
			});
		} finally {
			setDeletingId(null);
		}
	};

	return (
		<Box sx={(theme) => surfaceCard(theme)}>
			<SectionHeader
				icon={AttachFileOutlinedIcon}
				title="Files"
				action={
					<>
						<input
							ref={inputRef}
							type="file"
							hidden
							onChange={handleFileChange}
						/>
						<Button
							size="small"
							variant="outlined"
							onClick={handleChooseFile}
							disabled={uploading}
						>
							{uploading ? (
								<>
									<CircularProgress size={16} sx={{ mr: 1 }} />
									Uploading...
								</>
							) : (
								'Upload file'
							)}
						</Button>
					</>
				}
			/>
			<Divider sx={{ opacity: 0.6 }} />
			<Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
				{loading ? (
					<Stack alignItems="center" py={3}>
						<CircularProgress size={28} />
					</Stack>
				) : files.length === 0 ? (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ py: 2, px: 1 }}
					>
						No files attached to this job yet. Upload photos, specs, or other
						documents your team may need.
					</Typography>
				) : (
					<List disablePadding>
						{files.map((file) => (
							<ListItem
								key={file.id}
								disableGutters
								secondaryAction={
									<IconButton
										edge="end"
										aria-label={`Delete ${file.name}`}
										onClick={() => handleDelete(file.id)}
										disabled={deletingId === file.id}
									>
										{deletingId === file.id ? (
											<CircularProgress size={18} />
										) : (
											<DeleteOutlineIcon fontSize="small" />
										)}
									</IconButton>
								}
								sx={{
									px: 1,
									borderRadius: 1,
									'&:hover': { bgcolor: 'action.hover' },
								}}
							>
								<ListItemText
									primary={
										file.url ? (
											<Link
												href={file.url}
												target="_blank"
												rel="noopener noreferrer"
												underline="hover"
											>
												{file.name}
											</Link>
										) : (
											file.name
										)
									}
									secondary={[formatFileSize(file.size), file.uploaded_by_name]
										.filter(Boolean)
										.join(' — ')}
									primaryTypographyProps={{ fontWeight: 500 }}
								/>
							</ListItem>
						))}
					</List>
				)}
			</Box>
		</Box>
	);
};

export default JobFiles;
